import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { Linking, StyleSheet, View } from 'react-native';

import { isTablet } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Edges, SafeAreaView } from 'react-native-safe-area-context';
import {
    Camera,
    useCameraDevice,
    useCameraPermission,
    useCodeScanner,
} from 'react-native-vision-camera';
import { useDispatch, useSelector } from 'react-redux';

import { StackNavigationProp } from '@react-navigation/stack';

import { LANGUAGE_KEYS } from 'config/languageKeys';
import gStyles from 'config/styles';
import {
    LOGIN_FAILED_TITLE,
    QR_LOGIN_BACK_BUTTON,
    QR_LOGIN_BUTTON_GO_TO_SETTINGS,
    QR_LOGIN_CAMERA_NOT_ALLOWED_DESCRIPTION,
    QR_LOGIN_CAMERA_NOT_ALLOWED_TITLE,
} from 'config/testID';

import { SCREEN } from 'router/screens';
import { RouteNavigationParams } from 'router/types';

import { getBookmarks } from 'store/bookmarks/actions';
import { selectLayoutConfig } from 'store/config/selectors';
import { getMainMenuConfig } from 'store/railsBuilder/actions';
import {
    getPinsStatus,
    loginErrorReset,
    loginWithSessionToken,
} from 'store/user/actions';
import {
    getHaveToShowPinSetup,
    selectIsNonInteractiveUI,
    selectLoginError,
} from 'store/user/selectors';

import { RegularStorage, StorageKeys } from 'services/mmkv';
import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import useEnterTheApp from 'utils/hooks/appBootstrap/useEnterTheApp';
import useBackHandler from 'utils/hooks/useBackHandler.ts';
import useDebouncedSafeAreaInsets from 'utils/hooks/useDebouncedSafeAreaInsets';
import useNoInternet from 'utils/hooks/useNoInternet';

import ActionButton from 'components/CustomButtons/ActionButton';
import DialogBody from 'components/Dialog/components/DialogBody';
import ErrorDialog from 'components/ErrorDialog';
import Text, { ETextCategory, ETextColor } from 'components/Text';

import styles from './styles';

type Props = {
    navigation: StackNavigationProp<RouteNavigationParams>;
};

const {
    BACK_BUTTON,
    LOGIN: {
        LOGIN_QR_CODE_CAMERA_NOT_ALLOWED_TITLE,
        LOGIN_QR_CODE_CAMERA_NOT_ALLOWED_DESCRIPTION,
        BUTTON_GO_TO_SETTINGS,
        LOGIN_QR_CODE_CAMERA_VIEW_TITLE,
        ERROR_UNSUCCESSFUL_QR_CODE_LOGIN_TITLE,
        ERROR_UNSUCCESSFUL_QR_CODE_LOGIN_DESCRIPTION,
        ERROR_WRONG_QR_CODE_LOGIN_TITLE,
        ERROR_WRONG_QR_CODE_LOGIN_DESCRIPTION,
    },
} = LANGUAGE_KEYS;
const edges: Edges = [];

const QRLoginScreen = ({ navigation }: Props) => {
    const { hasPermission } = useCameraPermission();
    const device = useCameraDevice('back');
    const dispatch = useDispatch();
    const { isInternetConnected } = useNoInternet();

    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [isCameraShown, setIsCameraShown] = useState(hasPermission);

    const { bottom: bottomInset } = useDebouncedSafeAreaInsets();

    const { t } = useTranslation();
    const { background, primary } = useSelector(selectLayoutConfig);
    const haveToShowPinSetup = useSelector(getHaveToShowPinSetup);
    const isNonInteractive = useSelector(selectIsNonInteractiveUI);
    const loginError = useSelector(selectLoginError);

    const wasPinStatusCheckTriggered = useRef(false);

    const { onEnterTheApp } = useEnterTheApp();

    const errorContent = useMemo(
        () => ({
            title:
                loginError?.status === 500
                    ? ERROR_WRONG_QR_CODE_LOGIN_TITLE
                    : ERROR_UNSUCCESSFUL_QR_CODE_LOGIN_TITLE,
            description:
                loginError?.status === 500
                    ? ERROR_WRONG_QR_CODE_LOGIN_DESCRIPTION
                    : ERROR_UNSUCCESSFUL_QR_CODE_LOGIN_DESCRIPTION,
            buttonText: BACK_BUTTON,
        }),
        [loginError?.status],
    );

    const handlePinStatusCheckSuccess = useCallback(async () => {
        wasPinStatusCheckTriggered.current = true;

        const tutorialPageShown = await RegularStorage.getItem(
            StorageKeys.TUTORIAL_PAGE_SHOWN,
        );

        if (haveToShowPinSetup) {
            navigation.replace(SCREEN.PIN_SETUP);
        } else if (tutorialPageShown || isNonInteractive) {
            await onEnterTheApp();
        } else {
            navigation.replace(SCREEN.FTI_TUTORIAL);
        }
    }, [navigation, haveToShowPinSetup, onEnterTheApp, isNonInteractive]);

    const handleBackPress = useCallback(() => {
        setShowErrorDialog(false);

        setTimeout(() => {
            dispatch(loginErrorReset());
            navigation.goBack();
        }, 100);

        return true;
    }, []);

    const handleOpenSettings = useCallback(() => {
        Linking.openSettings();
    }, []);

    useBackHandler(handleBackPress);

    useEffect(() => {
        setShowErrorDialog(!!loginError);
    }, [loginError]);

    useEffect(() => {
        setIsCameraShown(hasPermission);
    }, [hasPermission]);

    const codeScanner = useCodeScanner({
        codeTypes: ['qr'],
        onCodeScanned: (codes) => {
            if (codes[0].value) {
                setIsCameraShown(false);
                dispatch(
                    loginWithSessionToken(codes[0].value, {
                        onSuccess: () => {
                            dispatch(getMainMenuConfig());
                            dispatch(getBookmarks());

                            const retryPinsStatusAction = () =>
                                new Promise<void>((resolve, reject) => {
                                    getPinsStatus({
                                        onSuccess: () => {
                                            handlePinStatusCheckSuccess();
                                            resolve();
                                        },
                                        onFailure: (error) => {
                                            if (isErrorNeedsRetry(error)) {
                                                reject(error);
                                            } else {
                                                setShowErrorDialog(true);
                                                resolve();
                                            }
                                        },
                                    });
                                });

                            dispatch(
                                getPinsStatus({
                                    onSuccess: handlePinStatusCheckSuccess,
                                    onFailure: (error) => {
                                        if (isErrorNeedsRetry(error)) {
                                            RetryController.showVitalDialog({
                                                actionButtonTitle:
                                                    LANGUAGE_KEYS.RELOAD_BUTTON,
                                                actionButtonOnPress:
                                                    retryPinsStatusAction,
                                            });
                                            RetryController.registerRetryCallback(
                                                retryPinsStatusAction,
                                                {
                                                    retryDuration:
                                                        10 * 60 * 1000,
                                                    maxRetryCount: 10,
                                                },
                                            );
                                            RetryController.executeRetry();
                                        } else {
                                            setShowErrorDialog(true);
                                        }
                                    },
                                }),
                            );
                        },
                    }),
                );
            }
        },
    });

    if (hasPermission) {
        return (
            <SafeAreaView
                style={[gStyles.container, { backgroundColor: background }]}
                edges={edges}
            >
                <View style={[styles.title, { backgroundColor: background }]}>
                    <Text
                        category={ETextCategory.title3}
                        color={ETextColor.neutral}
                        bold
                        center
                    >
                        {t(LOGIN_QR_CODE_CAMERA_VIEW_TITLE)}
                    </Text>
                </View>
                <View style={gStyles.container}>
                    {isCameraShown && device ? (
                        <Camera
                            device={device}
                            codeScanner={codeScanner}
                            style={StyleSheet.absoluteFillObject}
                            photoQualityBalance="speed"
                            isActive
                            photo
                        />
                    ) : null}
                </View>
                <View style={{ backgroundColor: background }}>
                    <ActionButton
                        accessible
                        accessibilityLabel={t(BACK_BUTTON)}
                        testID={QR_LOGIN_BACK_BUTTON}
                        title={BACK_BUTTON}
                        onPress={handleBackPress}
                        fillColor={background}
                        buttonTextStyle={styles.qrButtonText}
                        buttonStyle={[
                            styles.qrButton,
                            styles.button,
                            {
                                marginBottom: Math.max(24, bottomInset),
                            },
                        ]}
                    />
                </View>
                <ErrorDialog
                    visible={isInternetConnected && showErrorDialog}
                    animationType="fade"
                    title={errorContent.title}
                    titleTestID={LOGIN_FAILED_TITLE}
                    description={errorContent.description}
                    actionButtonTestID={errorContent.buttonText}
                    actionButtonTitle={errorContent.buttonText}
                    actionButtonFillColor={primary}
                    actionButtonTextStyle={{ color: background }}
                    actionButtonStyle={isTablet && styles.errorButton}
                    actionButtonOnPress={handleBackPress}
                />
            </SafeAreaView>
        );
    }

    return (
        <DialogBody
            title={LOGIN_QR_CODE_CAMERA_NOT_ALLOWED_TITLE}
            titleTestID={QR_LOGIN_CAMERA_NOT_ALLOWED_TITLE}
            description={LOGIN_QR_CODE_CAMERA_NOT_ALLOWED_DESCRIPTION}
            descriptionTestID={QR_LOGIN_CAMERA_NOT_ALLOWED_DESCRIPTION}
            actionButtonTextStyle={{ color: background }}
            revertButtons
            actionButtonTitle={BUTTON_GO_TO_SETTINGS}
            actionButtonAccessibilityLabel={t(BUTTON_GO_TO_SETTINGS)}
            handleActionButtonPress={handleOpenSettings}
            actionButtonFillColor={primary}
            actionButtonTestID={QR_LOGIN_BUTTON_GO_TO_SETTINGS}
            closeButtonTestID={QR_LOGIN_BACK_BUTTON}
            cancelButtonBordered
            cancelButtonTitle={BACK_BUTTON}
            cancelButtonAccessibilityLabel={t(BACK_BUTTON)}
            handleCancelPress={handleBackPress}
            cancelButtonFillColor={background}
            cancelButtonTextStyle={styles.backButtonText}
            style={styles.container}
        />
    );
};

export default QRLoginScreen;
